/**
 * Région d'annonce partagée — ce qui change à l'écran doit aussi s'entendre.
 *
 * POURQUOI
 * --------
 * Quand une action réussit et que son effet est visible sur place (une ligne qui change, un
 * compteur qui bouge), on n'affiche pas de toast : ce serait redondant avec ce que l'utilisateur
 * a sous les yeux. Mais « sous les yeux » ne vaut que pour qui regarde. Sans annonce, la même
 * action est parfaitement silencieuse pour un lecteur d'écran.
 *
 * UNE SEULE RÉGION, MONTÉE EN PERMANENCE
 * --------------------------------------
 * Une région vivante qui apparaît AVEC son contenu n'est pas annoncée par la plupart des
 * lecteurs d'écran : ils observent les mutations d'un nœud déjà présent. Elle est donc montée
 * vide au démarrage, à côté du `<Toaster/>`, et seul son texte change.
 *
 * `polite` et non `assertive` : ces messages confirment, ils n'alertent pas — ils ne doivent pas
 * couper la lecture en cours. Les erreurs, elles, passent par `role="alert"` d'`ErrorState`.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react'

type Annoncer = (message: string) => void

const Ctx = createContext<Annoncer>(() => {})

export function RegionVivanteProvider({ children }: { children: ReactNode }): JSX.Element {
  const [message, setMessage] = useState('')
  const dernier = useRef('')

  const annoncer = useCallback<Annoncer>((texte) => {
    // Deux annonces identiques d'affilée ne produiraient qu'une seule lecture : le nœud ne
    // change pas. On insère une espace fine invisible pour forcer la mutation — c'est le cas
    // réel du « trié par X » cliqué deux fois, ou d'un enregistrement répété.
    const suffixe = texte === dernier.current ? ' ' : ''
    dernier.current = texte
    setMessage(texte + suffixe)
  }, [])

  return (
    <Ctx.Provider value={annoncer}>
      {children}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true"
           data-testid="region-vivante">
        {message}
      </div>
    </Ctx.Provider>
  )
}

/** Annonce un changement aux technologies d'assistance. Sans effet visuel. */
export function useAnnonce(): Annoncer {
  return useContext(Ctx)
}

/**
 * Regroupe les changements rapides avant de les confier à l'unique région
 * vivante. Les listes et timelines continuent de se mettre à jour visuellement,
 * mais un lecteur d'écran n'entend que le dernier résumé de la fenêtre.
 *
 * Un rejeu peut livrer un historique entier d'un coup. `suspendre` garde alors
 * uniquement le dernier résumé, et `reprendreAvecResume` le publie une fois le
 * rejeu terminé, sans laisser la minuterie survivre au composant.
 */
export function useAnnonceBornee(fenetreMs = 2_000): {
  annoncer: Annoncer
  suspendre: () => void
  reprendreAvecResume: () => void
} {
  const annoncerDansRegion = useAnnonce()
  const attente = useRef<string | undefined>(undefined)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const suspendue = useRef(false)

  const vider = useCallback(() => {
    timer.current = undefined
    const message = attente.current
    attente.current = undefined
    if (message && !suspendue.current) annoncerDansRegion(message)
  }, [annoncerDansRegion])

  const annoncer = useCallback<Annoncer>((message) => {
    attente.current = message
    if (suspendue.current || timer.current !== undefined) return
    timer.current = setTimeout(vider, fenetreMs)
  }, [fenetreMs, vider])

  const suspendre = useCallback(() => {
    suspendue.current = true
    if (timer.current !== undefined) {
      clearTimeout(timer.current)
      timer.current = undefined
    }
  }, [])

  const reprendreAvecResume = useCallback(() => {
    suspendue.current = false
    vider()
  }, [vider])

  useEffect(() => () => {
    if (timer.current !== undefined) clearTimeout(timer.current)
  }, [])

  return useMemo(() => ({ annoncer, suspendre, reprendreAvecResume }), [annoncer, reprendreAvecResume, suspendre])
}
